import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import CtaBand from '@/components/feature/CtaBand';
import SiteLayout from '@/components/feature/SiteLayout';
import { useSeo } from '@/hooks/useSeo';
import ArticleToc from '@/pages/archives/detail/components/ArticleToc';
import ShareBar from '@/pages/archives/detail/components/ShareBar';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  const tocItems = useMemo(
    () => (article ? article.sections.map((s, i) => ({ id: `section-${i + 1}`, label: s.heading })) : []),
    [article],
  );

  const related = useMemo(() => {
    if (!article) return [];
    const sameCategory = articles.filter((a) => a.slug !== article.slug && a.category === article.category);
    const others = articles.filter((a) => a.slug !== article.slug && a.category !== article.category);
    return [...sameCategory, ...others].slice(0, 3);
  }, [article]);

  const jsonLd = useMemo(() => {
    if (!article) return undefined;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: article.title,
        description: article.excerpt,
        image: article.cover,
        datePublished: article.date,
        author: { '@type': 'Organization', name: article.author },
        articleSection: article.categoryName,
        keywords: article.tags.join(','),
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://readdy.ai/' },
          { '@type': 'ListItem', position: 2, name: '内幕档案', item: 'https://readdy.ai/archives' },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
    ];
  }, [article]);

  useSeo({
    title: article ? `${article.title}｜缅北黑料 · 反诈内幕档案库` : '档案未找到｜缅北黑料',
    description: article ? article.excerpt : '你访问的档案不存在或已下线。',
    keywords: article ? `缅北黑料,${article.tags.join(',')}` : '缅北黑料',
    path: `/archives/${slug ?? ''}`,
    jsonLd,
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-content flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">没有找到这篇档案</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            它可能已被合并或下线。你可以回到档案列表继续浏览其他内容。
          </p>
          <Link
            to="/archives"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800"
          >
            返回内幕档案
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <article>
        <header className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
            <Breadcrumbs items={[{ label: '内幕档案', to: '/archives' }, { label: article.title }]} />
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                {article.categoryName}
              </span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>
            <h1 className="mt-5 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {article.excerpt}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" aria-hidden="true"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true"></i>
                阅读约 {article.readTime}
              </span>
            </div>
          </div>
        </header>

        <div className="bg-background-50">
          <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-8">
              <div className="h-64 w-full overflow-hidden rounded-lg md:h-96">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 缅北黑料内幕档案`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="mt-10 space-y-10">
                {article.sections.map((section, index) => (
                  <section key={section.heading} id={`section-${index + 1}`} className="scroll-mt-28">
                    <h2 className="font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                      {section.heading}
                    </h2>
                    <div className="mt-4 space-y-4">
                      {section.paragraphs.map((p) => (
                        <p key={p} className="text-sm leading-[1.9] text-foreground-700 md:text-base">
                          {p}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-10 flex flex-col gap-4 rounded-lg border border-primary-200 bg-primary-50 p-5 md:flex-row md:items-center md:justify-between md:p-6">
                <p className="flex items-start gap-3 text-sm leading-relaxed text-primary-900">
                  <i className="ri-lightbulb-line mt-0.5 text-lg text-primary-600" aria-hidden="true"></i>
                  <span>如果你或身边的人正在经历类似情况，请先保存证据并联系官方渠道，不要先转账。</span>
                </p>
                <ShareBar title={article.title} />
              </div>

              <div className="mt-8">
                <Link
                  to="/archives"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true"></i>
                  返回内幕档案列表
                </Link>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-28 space-y-6">
                <ArticleToc items={tocItems} />
                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="font-heading text-sm font-bold text-foreground-950">合规与隐私说明</h2>
                  <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                    本文案例来自公开报道与经授权的讲述，姓名、地点与时间均已做匿名化处理，仅用于反诈科普。
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-16 md:px-6 md:py-20">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-accent-600">
                <span className="h-px w-6 bg-accent-400" aria-hidden="true"></span>
                相关阅读
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950">建议接着看这几篇</h2>
            </div>
            <Link
              to="/archives"
              className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              浏览全部档案
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <ArticleCard key={item.slug} article={item} />
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </SiteLayout>
  );
}